/**
 * Adapted from @atlassian/preview-panel-global-objects/src/global-object-preview-panels.tsx
 * Since we can't use the @atlassian/preview-panel-global-objects package in ai-mate packages
 * as it introduces a circular dependency.
 */
import React, { useCallback } from 'react';

import { defineMessages, useIntl } from 'react-intl';

import WorkItemIcon from '@atlaskit/icon/core/work-item';
import type { AnalyticsAttributes } from '@atlassian/analytics-bridge';
import { usePreviewPanelsActions } from '@atlassian/preview-panels-api';

import { JIRA_PRODUCT, WORK_ITEM_GLOBAL_OBJECT_TYPE, WORK_ITEM_PANEL_TYPE } from './constants';
import { IFramePreviewPanel } from './panels/iFramePreviewPanel';

type OpenPreviewPanel = (props: {
	analyticsAttributes?: AnalyticsAttributes;
	ari: string;
	loadingState?: () => React.ReactNode;
	name: string;
	parentProduct?: string;
	url: string;
}) => void;

type ReturnType = {
	openPreviewPanel: OpenPreviewPanel;
};

const messages = defineMessages({
	workItem: {
		id: 'navigation.preview-panel-global-objects.workItem',
		defaultMessage: 'Jira work item',
		description: 'Title for the work item preview panel',
	},
});

// Helper function to check if we're in an iframe - exported for testing
const isInIframe = (): boolean => {
	try {
		return window !== window.top;
	} catch {
		// If we can't access window.top due to cross-origin restrictions, assume we're in an iframe
		return true;
	}
};

/**
 * Constructs a proper embed URL by extracting query parameters and reconstructing the URL
 * @param url - The original URL
 * @param supportedObjects - Array of supported object types for previewPanels param
 * @returns A properly formatted embed URL with all query parameters at the end
 */
const buildIssueViewEmbedUrl = (
	url: string,
	supportedObjects: string[] = ['jira-issue'],
): string => {
	try {
		const urlObj = new URL(url);

		// Ensure there's no trailing slash before appending '/embed'
		urlObj.pathname = urlObj.pathname.replace(/\/$/, '') + '/embed';

		// Add previewPanels to existing search params
		urlObj.searchParams.set('previewPanels', supportedObjects.join(','));
		// Return the full URL as a string
		return urlObj.toString();
	} catch {
		// Fallback for invalid URLs
		return `${url}/embed?previewPanels=${encodeURIComponent(supportedObjects.join(','))}`;
	}
};

export const useWorkItemPreviewPanel = (): ReturnType => {
	const { formatMessage } = useIntl();
	const { open } = usePreviewPanelsActions();

	const openPreviewPanel = useCallback(
		({
			url,
			ari,
			name,
			loadingState,
			analyticsAttributes,
			parentProduct,
		}: {
			analyticsAttributes?: AnalyticsAttributes;
			ari: string;
			loadingState?: () => React.ReactNode;
			name: string;
			parentProduct?: string;
			url: string;
		}): void => {
			// Check if we're in an iframe (not the top-most frame)
			if (isInIframe()) {
				// Post message to parent frame to open the panel
				window.parent.postMessage(
					{
						type: 'OPEN_PREVIEW_PANEL',
						payload: {
							url,
							ari,
							name,
							analyticsAttributes,
						},
					},
					'*',
				);
				return;
			}

			open({
				panel: {
					id: ari,
					type: WORK_ITEM_GLOBAL_OBJECT_TYPE,
					content: () => (
						<IFramePreviewPanel
							name={formatMessage(messages.workItem)}
							url={url}
							iframeUrl={buildIssueViewEmbedUrl(url, [WORK_ITEM_GLOBAL_OBJECT_TYPE])}
							icon={<WorkItemIcon label="" />}
							panelType={WORK_ITEM_PANEL_TYPE}
							product={JIRA_PRODUCT}
							parentProduct={parentProduct}
							ari={ari}
							analyticsAttributes={analyticsAttributes}
							loadingState={loadingState}
						/>
					),
					product: JIRA_PRODUCT,
				},
			});
		},
		[open, formatMessage],
	);

	return {
		openPreviewPanel,
	};
};
